import { useCallback, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import { asyncGetAccountByDocument } from '../../../../services/accounts.service';
import { AccountAddress } from '../../../../shared/types/modules/account';
import { ToastSwal } from '../../../components/general/toastSwals/toastSwal';
import { ORBIT_ACCOUNT_MIN_DOCUMENT_LENGTH } from '../constants/accountLookup';
import { OrbitCitizenInfoValues } from '../citizenInfo/formData';
import { OrbitSpecialConditionValues } from '../specialCondition/formData';
import { OrbitAccountLookupParams } from '../sectionProps';
import {
    mapAccountToAddress,
    mapAccountToCitizenInfo,
    mapAccountToSpecialCondition,
} from '../utils/mapAccountToOrbitForm';
import { useOrbitAccountLookupCatalogs } from './useOrbitAccountLookupCatalogs';

type FormikRefs = {
    formikCitizenInfo?: FormikProps<OrbitCitizenInfoValues>;
    formikAddress?: FormikProps<AccountAddress>;
    formikSpecialCondition?: FormikProps<OrbitSpecialConditionValues>;
};

export const useOrbitAccountLookup = ({
    formikCitizenInfo,
    formikAddress,
    formikSpecialCondition,
    readOnly,
}: FormikRefs & { readOnly?: boolean }) => {
    const [lookingUpAccount, setLookingUpAccount] = useState(false);
    const lastLookupKeyRef = useRef('');

    const { catalogs, ready: catalogsReady, error: catalogsError } =
        useOrbitAccountLookupCatalogs(!readOnly);

    const lookupAccountByDocument = useCallback(
        async ({ typeDocument, document }: OrbitAccountLookupParams) => {
            const trimmedTypeDocument = typeDocument.trim();
            const trimmedDocument = document.trim();
            const lookupKey = `${trimmedTypeDocument}:${trimmedDocument}`;

            if (
                readOnly ||
                !trimmedTypeDocument ||
                !trimmedDocument ||
                trimmedDocument.length < ORBIT_ACCOUNT_MIN_DOCUMENT_LENGTH ||
                lookupKey === lastLookupKeyRef.current
            ) {
                return;
            }

            if (!catalogsReady) {
                return;
            }

            if (catalogsError) {
                ToastSwal.fire({
                    text: 'No se pudieron cargar los catálogos para consultar la cuenta. Intenta nuevamente.',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
                return;
            }

            setLookingUpAccount(true);

            try {
                const account = await asyncGetAccountByDocument(trimmedDocument);
                lastLookupKeyRef.current = lookupKey;

                if (!account) {
                    ToastSwal.fire({
                        text: 'No se encontró una cuenta con este documento, debe diligenciar los datos del ciudadano.',
                        icon: 'info',
                        timer: 8000,
                        position: 'center-end',
                    });
                    return;
                }

                const citizenValues = mapAccountToCitizenInfo(
                    account,
                    catalogs.genders
                );
                const addressValues = mapAccountToAddress(
                    account,
                    catalogs.cities,
                    catalogs.addressTypes
                );
                const specialConditionValues = mapAccountToSpecialCondition(
                    account,
                    catalogs.specialConditions
                );

                formikCitizenInfo?.setValues({
                    ...formikCitizenInfo.values,
                    ...citizenValues,
                    typeDocument: trimmedTypeDocument,
                    document: trimmedDocument,
                });

                if (formikAddress && Object.keys(addressValues).length > 0) {
                    formikAddress.setValues({
                        ...formikAddress.values,
                        ...addressValues,
                    });
                }

                if (
                    formikSpecialCondition &&
                    Object.keys(specialConditionValues).length > 0
                ) {
                    formikSpecialCondition.setValues({
                        ...formikSpecialCondition.values,
                        ...specialConditionValues,
                    });
                }
            } catch {
                ToastSwal.fire({
                    text: 'No se pudo consultar la cuenta. Intenta nuevamente.',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
            } finally {
                setLookingUpAccount(false);
            }
        },
        [
            catalogs,
            catalogsError,
            catalogsReady,
            formikAddress,
            formikCitizenInfo,
            formikSpecialCondition,
            readOnly,
        ]
    );

    const resetAccountLookup = useCallback(() => {
        lastLookupKeyRef.current = '';
        setLookingUpAccount(false);
    }, []);

    return {
        lookingUpAccount,
        lookupAccountByDocument,
        resetAccountLookup,
        catalogsReady,
    };
};
